"use client";

import clsx from "clsx";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";

/**
 * Live validity banner shown at the top of every exercise builder modal.
 *
 * Drives off a list of `ValidityCheck` entries. Each entry has a severity
 * (`ok` | `warn` | `error`) which controls the dot colour + icon.
 *
 * The banner is intentionally inline (not a modal). It updates as the
 * admin types so the state of the exercise is always visible.
 */
export type ValiditySeverity = "ok" | "warn" | "error";

export interface ValidityCheck {
  label: string;
  severity: ValiditySeverity;
}

interface FormValidityBannerProps {
  checks: ValidityCheck[];
  /** Optional headline above the checks list. */
  title?: string;
  className?: string;
}

const SEVERITY_META: Record<
  ValiditySeverity,
  { dot: string; icon: React.ComponentType<{ size?: number; className?: string }>; row: string }
> = {
  ok: {
    dot: "bg-[#34C759]",
    icon: CheckCircle2,
    row: "text-[#FAFAFA]",
  },
  warn: {
    dot: "bg-[#FF9F0A]",
    icon: AlertTriangle,
    row: "text-[#FAFAFA]",
  },
  error: {
    dot: "bg-[#FF453A]",
    icon: XCircle,
    row: "text-[#FAFAFA]",
  },
};

export function FormValidityBanner({ checks, title, className }: FormValidityBannerProps) {
  if (checks.length === 0) return null;

  const errorCount = checks.filter((c) => c.severity === "error").length;
  const warnCount = checks.filter((c) => c.severity === "warn").length;
  const okCount = checks.filter((c) => c.severity === "ok").length;

  // Overall border tone — red if any error, amber if any warn, green if all ok.
  const tone =
    errorCount > 0
      ? "border-[#FF453A]/40 bg-[#FF453A]/10"
      : warnCount > 0
        ? "border-[#FF9F0A]/40 bg-[#FF9F0A]/10"
        : "border-[#34C759]/30 bg-[#34C759]/10";

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx("rounded-lg border px-3 py-2", tone, className)}
    >
      {title && (
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#8E8E93] mb-1.5">
          {title}
          <span className="ml-2 text-[#8E8E93]/60">
            {okCount}/{checks.length} ok
            {warnCount > 0 ? ` · ${warnCount} warn` : ""}
            {errorCount > 0 ? ` · ${errorCount} error` : ""}
          </span>
        </p>
      )}
      <ul className="flex flex-col gap-1">
        {checks.map((c, i) => {
          const meta = SEVERITY_META[c.severity];
          const Icon = meta.icon;
          return (
            <li
              key={`${c.severity}-${i}`}
              className={clsx("flex items-center gap-2 text-xs", meta.row)}
            >
              <span className={clsx("h-1.5 w-1.5 rounded-full shrink-0", meta.dot)} aria-hidden />
              <Icon size={12} className="shrink-0" aria-hidden />
              <span>{c.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}